import type { Runtime } from './runtime';
import { optimisticAppointment } from './runtime';
import type { OutboxEntry } from './outbox/entry';
import type { DayHours } from '@yourstore/domain';
import type { WireIdentity } from './wire';

/**
 * Every write the staff can make, as a function that appends one intent.
 *
 * **This is the entire write surface of the app.** No view calls `api.request`
 * and no view calls `mirror.put*`. A tap produces an intent; the outbox does the
 * rest. Spec §8.2 describes that as the offline behaviour, but building it as
 * the *only* behaviour is what makes it trustworthy — the path that runs in
 * airplane mode is the path that ran at the counter this morning with four bars
 * of signal.
 *
 * Each function's job is small and identical in shape: name the intent, carry
 * the request fields `core-api`'s wire schema expects, and snapshot the human
 * facts the task list would need if this one comes back rejected.
 */

export interface BookInput {
  serviceId: string;
  serviceName: string;
  durationMin: number;
  resourceId: string;
  resourceName: string | null;
  startIso: string;
  partySize?: number | undefined;
  notes?: string | undefined;
  source: 'staff' | 'walk_in';
  /** Somebody already in the book… */
  clientId?: string | undefined;
  /** …or the person standing at the counter. Exactly one of the two. */
  guest?: { name: string; identities: WireIdentity[]; notes?: string | undefined } | undefined;
  clientName: string;
}

export function createActions(runtime: Runtime, now: () => number = Date.now) {
  const { outbox, mirror } = runtime;

  return {
    async book(input: BookInput): Promise<OutboxEntry> {
      const session = await mirror.session();
      // No `price_cents`, no `end`, no `duration_min` on the request. The
      // server looks all three up from `service_id` (obligation B7 and its
      // duration sibling), and an offline device is the last thing that should
      // be asserting what something costs.
      const payload: Record<string, unknown> = {
        service_id: input.serviceId,
        resource_id: input.resourceId,
        start: input.startIso,
        source: input.source,
        ...(input.partySize === undefined ? {} : { party_size: input.partySize }),
        ...(input.notes === undefined ? {} : { notes: input.notes }),
        ...(input.clientId === undefined ? {} : { client_id: input.clientId }),
        ...(input.guest === undefined ? {} : { guest: input.guest }),
        ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
      };

      return outbox.enqueue({
        kind: 'appointment.create',
        payload,
        describe: {
          what: `${input.clientName} — ${input.serviceName}`,
          person: input.clientName,
          whenIso: input.startIso,
        },
        optimistic: optimisticAppointment({
          serviceId: input.serviceId,
          serviceName: input.serviceName,
          resourceId: input.resourceId,
          resourceName: input.resourceName,
          startIso: input.startIso,
          durationMin: input.durationMin,
          clientId: input.clientId ?? null,
          clientName: input.clientName,
          source: input.source,
          ...(input.partySize === undefined ? {} : { partySize: input.partySize }),
          notes: input.notes,
          nowIso: new Date(now()).toISOString(),
        }),
      });
    },

    async reschedule(input: {
      appointmentId: string;
      startIso: string;
      resourceId?: string | undefined;
      clientName: string;
      serviceName: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.reschedule',
        target: { appointment_id: input.appointmentId },
        payload: {
          start: input.startIso,
          ...(input.resourceId === undefined ? {} : { resource_id: input.resourceId }),
        },
        describe: {
          what: `Moving ${input.clientName} — ${input.serviceName}`,
          person: input.clientName,
          whenIso: input.startIso,
        },
      });
    },

    async cancel(input: {
      appointmentId: string;
      clientName: string;
      whenIso: string;
      feeCents?: number | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.cancel',
        target: { appointment_id: input.appointmentId },
        payload: input.feeCents === undefined ? {} : { fee_cents: input.feeCents },
        describe: {
          what: `Cancelling ${input.clientName}`,
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    async setStatus(input: {
      appointmentId: string;
      status: 'completed' | 'no_show';
      clientName: string;
      whenIso: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'appointment.status',
        target: { appointment_id: input.appointmentId },
        payload: { status: input.status },
        describe: {
          what: `Marking ${input.clientName} ${input.status === 'completed' ? 'done' : 'a no-show'}`,
          person: input.clientName,
          whenIso: input.whenIso,
        },
      });
    },

    async createClient(input: {
      name: string;
      identities: WireIdentity[];
      notes?: string | undefined;
      tags?: string[] | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.create',
        payload: {
          name: input.name,
          identities: input.identities,
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          ...(input.tags === undefined ? {} : { tags: input.tags }),
        },
        describe: { what: `Adding ${input.name}`, person: input.name },
      });
    },

    async updateClient(input: {
      clientId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.update',
        target: { client_id: input.clientId },
        payload: input.changes,
        describe: { what: `Editing ${input.name}`, person: input.name },
      });
    },

    async archiveClient(input: {
      clientId: string;
      name: string;
      archived: boolean;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.archive',
        target: { client_id: input.clientId },
        payload: { archived: input.archived },
        describe: {
          what: `${input.archived ? 'Archiving' : 'Restoring'} ${input.name}`,
          person: input.name,
        },
      });
    },

    async addIdentity(input: {
      clientId: string;
      name: string;
      identity: WireIdentity;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.identity.add',
        target: { client_id: input.clientId },
        payload: { ...input.identity },
        describe: { what: `Adding a contact for ${input.name}`, person: input.name },
      });
    },

    async removeIdentity(input: {
      clientId: string;
      name: string;
      identity: WireIdentity;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'client.identity.remove',
        target: { client_id: input.clientId },
        payload: { ...input.identity },
        describe: { what: `Removing a contact for ${input.name}`, person: input.name },
      });
    },

    async addPreference(input: {
      clientId: string;
      name: string;
      category: 'preference' | 'allergy';
      label: string;
      detail?: string | undefined;
      internal: boolean;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'preference.create',
        target: { client_id: input.clientId },
        payload: {
          category: input.category,
          label: input.label,
          internal: input.internal,
          ...(input.detail === undefined ? {} : { detail: input.detail }),
        },
        describe: { what: `Noting "${input.label}" for ${input.name}`, person: input.name },
      });
    },

    async deletePreference(input: {
      clientId: string;
      preferenceId: string;
      name: string;
      label: string;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'preference.delete',
        target: { client_id: input.clientId, preference_id: input.preferenceId },
        payload: {},
        describe: { what: `Removing "${input.label}" from ${input.name}`, person: input.name },
      });
    },

    async createService(input: {
      name: string;
      priceCents: number;
      durationMin: number;
      resourceIds: string[];
      description?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'service.create',
        payload: {
          name: input.name,
          price_cents: input.priceCents,
          duration_min: input.durationMin,
          resource_ids: input.resourceIds,
          ...(input.description === undefined ? {} : { description: input.description }),
        },
        describe: { what: `Adding ${input.name} to the menu` },
      });
    },

    async updateService(input: {
      serviceId: string;
      name: string;
      changes: Record<string, unknown>;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'service.update',
        target: { service_id: input.serviceId },
        payload: input.changes,
        describe: { what: `Editing ${input.name}` },
      });
    },

    /**
     * Hours are a whole week, always.
     *
     * `PUT /v1/settings/hours` takes all seven days and `catalog.ts` says why:
     * patching one day invites two devices each editing "their" day offline and
     * a merge producing a week neither person chose. Whole-week replacement
     * makes a collision visible as a collision, which is the only outcome §8.3
     * permits.
     */
    async setHours(hours: DayHours[]): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'hours.set',
        payload: { hours },
        describe: { what: 'Changing opening hours' },
      });
    },

    async createBlackout(input: {
      startIso: string;
      endIso: string;
      resourceId?: string | null;
      reason?: string | undefined;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'blackout.create',
        payload: {
          start: input.startIso,
          end: input.endIso,
          resource_id: input.resourceId ?? null,
          ...(input.reason === undefined ? {} : { reason: input.reason }),
        },
        describe: {
          what: input.reason === undefined ? 'Closing the calendar' : `Closing for ${input.reason}`,
          whenIso: input.startIso,
        },
      });
    },

    async deleteBlackout(input: {
      blackoutId: string;
      reason: string | null;
    }): Promise<OutboxEntry> {
      return outbox.enqueue({
        kind: 'blackout.delete',
        target: { blackout_id: input.blackoutId },
        payload: {},
        describe: { what: `Reopening ${input.reason ?? 'the calendar'}` },
      });
    },
  };
}

export type Actions = ReturnType<typeof createActions>;
